/**
 * Configuration Stripe pour B8ild
 *
 * Contient les constantes des plans, prix et limites
 */

// Types pour les plans
export type PlanType = 'free' | 'starter' | 'pro' | 'entreprise';
export type BillingPeriod = 'monthly' | 'yearly';

// Interface pour les limites d'un plan
export interface PlanLimits {
  max_chantiers: number;
  max_users: number;
  max_membres: number;
  max_analyses_ia: number;
  max_ocr: number;
  historique_annees: number;
}

// Interface pour les prix d'un plan
export interface PlanPricing {
  monthly: number;
  yearly: number;
  monthlyEquivalent: number; // Prix mensuel equivalent pour l'annuel
}

// Interface complete d'un plan
export interface Plan {
  id: PlanType;
  name: string;
  description: string;
  limits: PlanLimits;
  pricing: PlanPricing;
  features: string[];
  popular?: boolean;
}

// =============================================================================
// LIMITES PAR PLAN
// -1 signifie illimite
// =============================================================================

export const PLAN_LIMITS: Record<PlanType, PlanLimits> = {
  free: {
    max_chantiers: 3,
    max_users: 1,
    max_membres: 5,
    max_analyses_ia: 10,
    max_ocr: 20,
    historique_annees: 1,
  },
  starter: {
    max_chantiers: 5,
    max_users: 2,
    max_membres: 10,
    max_analyses_ia: 60,
    max_ocr: 100,
    historique_annees: 2,
  },
  pro: {
    max_chantiers: 15,
    max_users: 5,
    max_membres: 30,
    max_analyses_ia: 240,
    max_ocr: 450,
    historique_annees: 5,
  },
  entreprise: {
    max_chantiers: -1,
    max_users: 15,
    max_membres: -1,
    max_analyses_ia: -1,
    max_ocr: -1,
    historique_annees: -1,
  },
};

// =============================================================================
// PRIX PAR PLAN (en euros)
// =============================================================================

export const PLAN_PRICES: Record<Exclude<PlanType, 'free'>, PlanPricing> = {
  starter: {
    monthly: 49,
    yearly: 468,
    monthlyEquivalent: 39, // 468 / 12 = 39
  },
  pro: {
    monthly: 99,
    yearly: 948,
    monthlyEquivalent: 79, // 948 / 12 = 79
  },
  entreprise: {
    monthly: 149,
    yearly: 1428,
    monthlyEquivalent: 119, // 1428 / 12 = 119
  },
};

// =============================================================================
// CONFIGURATION COMPLETE DES PLANS
// =============================================================================

export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Gratuit',
    description: 'Pour decouvrir B8ild',
    limits: PLAN_LIMITS.free,
    pricing: { monthly: 0, yearly: 0, monthlyEquivalent: 0 },
    features: [
      '3 chantiers actifs',
      '5 membres d\'equipe',
      '10 analyses IA / mois',
      '20 scans IA / mois',
      'Historique 1 an',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    description: 'Pour les petites equipes BTP',
    limits: PLAN_LIMITS.starter,
    pricing: PLAN_PRICES.starter,
    features: [
      '5 chantiers actifs',
      '10 membres d\'equipe',
      '60 analyses IA / mois',
      '100 scans IA / mois',
      'Historique 2 ans',
      'Support email (48h)',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Pour les PME du BTP',
    limits: PLAN_LIMITS.pro,
    pricing: PLAN_PRICES.pro,
    popular: true,
    features: [
      '15 chantiers actifs',
      '30 membres d\'equipe',
      '240 analyses IA / mois',
      '450 scans IA / mois',
      'Historique 5 ans',
      'Support prioritaire',
      'Export PDF personnalise',
    ],
  },
  {
    id: 'entreprise',
    name: 'Entreprise',
    description: 'Pour les grandes entreprises',
    limits: PLAN_LIMITS.entreprise,
    pricing: PLAN_PRICES.entreprise,
    features: [
      'Chantiers illimites',
      'Membres illimites',
      'Analyses IA illimitees',
      'Scans IA illimites',
      'Historique illimite',
      'Support dedie',
      'Formation personnalisee',
      'API access',
    ],
  },
];

// =============================================================================
// FONCTIONS UTILITAIRES
// =============================================================================

/**
 * Obtenir un plan par son ID
 */
export function getPlan(planId: PlanType): Plan | undefined {
  return PLANS.find(p => p.id === planId);
}

/**
 * Obtenir les limites d'un plan
 */
export function getPlanLimits(planId: PlanType): PlanLimits {
  return PLAN_LIMITS[planId] || PLAN_LIMITS.free;
}

/**
 * Formater une limite pour l'affichage
 * -1 devient "Illimite"
 */
export function formatLimit(value: number): string {
  if (value === -1) return 'Illimite';
  return value.toString();
}

/**
 * Calculer l'economie en pourcentage pour l'abonnement annuel
 */
export function calculateYearlySavings(planId: Exclude<PlanType, 'free'>): number {
  const pricing = PLAN_PRICES[planId];
  const monthlyTotal = pricing.monthly * 12;
  const savings = ((monthlyTotal - pricing.yearly) / monthlyTotal) * 100;
  return Math.round(savings);
}

/**
 * Verifier si une limite est atteinte
 * @param currentValue - Valeur actuelle
 * @param limit - Limite du plan (-1 = illimite)
 * @returns true si la limite n'est pas atteinte
 */
export function isWithinLimit(currentValue: number, limit: number): boolean {
  if (limit === -1) return true; // Illimite
  return currentValue < limit;
}

/**
 * Calculer le pourcentage d'usage
 * @param used - Nombre utilise
 * @param limit - Limite (-1 = illimite)
 * @returns Pourcentage entre 0 et 100
 */
export function calculateUsagePercentage(used: number, limit: number): number {
  if (limit === -1) return 0; // Illimite = jamais plein
  if (limit === 0) return 100;
  return Math.min(100, Math.round((used / limit) * 100));
}

/**
 * Obtenir la couleur selon le pourcentage d'usage
 */
export function getUsageColor(percentage: number): 'green' | 'yellow' | 'orange' | 'red' {
  if (percentage < 50) return 'green';
  if (percentage < 75) return 'yellow';
  if (percentage < 90) return 'orange';
  return 'red';
}

/**
 * Generer le lookup_key pour Stripe
 */
export function getLookupKey(plan: Exclude<PlanType, 'free'>, period: BillingPeriod): string {
  return `${plan}_${period}`;
}

/**
 * Verifier si un plan peut etre downgrade vers un autre
 * On ne peut pas downgrade si on depasse les limites du plan cible
 */
export function canDowngrade(
  targetPlan: PlanType,
  currentUsage: {
    chantiers: number;
    users: number;
    membres: number;
  }
): { allowed: boolean; reason?: string } {
  const targetLimits = PLAN_LIMITS[targetPlan];

  if (targetLimits.max_chantiers !== -1 && currentUsage.chantiers > targetLimits.max_chantiers) {
    return {
      allowed: false,
      reason: `Vous avez ${currentUsage.chantiers} chantiers actifs. Le plan ${targetPlan} est limite a ${targetLimits.max_chantiers}.`,
    };
  }

  if (targetLimits.max_users !== -1 && currentUsage.users > targetLimits.max_users) {
    return {
      allowed: false,
      reason: `Vous avez ${currentUsage.users} utilisateurs. Le plan ${targetPlan} est limite a ${targetLimits.max_users}.`,
    };
  }

  if (targetLimits.max_membres !== -1 && currentUsage.membres > targetLimits.max_membres) {
    return {
      allowed: false,
      reason: `Vous avez ${currentUsage.membres} membres. Le plan ${targetPlan} est limite a ${targetLimits.max_membres}.`,
    };
  }

  return { allowed: true };
}
